import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Marker, InfoWindow, Polygon, Polyline } from '@react-google-maps/api';
import config from '../../../config';
import { useGoogleMaps } from '../../../context/GoogleMapsProvider';
import { getDriverIcon, getBookingIcon, getDropIcon, getPincodeIcon, getPincodeTriangleIcon } from './markerIcons';
import { computePixelClusters } from './pixelCluster';
import LeadDestinationHeatOverlay from './LeadDestinationHeatOverlay';



const containerStyle = {
    width: '100%',
    height: '100%',
};

// Default center (Bangalore)
const defaultCenter = {
    lat: 12.9716,
    lng: 77.5946,
};

const options = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
};

// Pixel cluster radius — markers within this many screen pixels get grouped
const PIXEL_CLUSTER_RADIUS = 36;
// Cluster click should feel like a normal map zoom, not a street-level jump.
const CLUSTER_CLICK_MAX_ZOOM = 16;
const CLUSTER_CLICK_MIN_SPAN_DEG = 0.0018; // ~200m — keeps co-located trucks from forcing absurd zoom

/** Zoom that fits `bounds` in the current map viewport (with padding). */
const estimateZoomForBounds = (map, bounds) => {
    const div = map.getDiv();
    const pad = 100;
    const width = Math.max(64, (div?.clientWidth || 800) - pad * 2);
    const height = Math.max(64, (div?.clientHeight || 600) - pad * 2);
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const lngSpan = Math.max(Math.abs(ne.lng() - sw.lng()), CLUSTER_CLICK_MIN_SPAN_DEG);
    const latSpan = Math.max(Math.abs(ne.lat() - sw.lat()), CLUSTER_CLICK_MIN_SPAN_DEG * 0.75);
    const zoomLng = Math.log2((width * 360) / (lngSpan * 256));
    const zoomLat = Math.log2((height * 180) / (latSpan * 256));
    return Math.floor(Math.min(zoomLng, zoomLat, CLUSTER_CLICK_MAX_ZOOM));
};

// Round lat/lng to 5 decimal places (~1m precision) for grouping co-located bookings
const locationKey = (lat, lng) => `${parseFloat(lat).toFixed(5)}_${parseFloat(lng).toFixed(5)}`;
const getBookingId = (booking) => booking?.id ?? booking?.booking_id;

const toMapPoint = (latValue, lngValue, extra = {}) => {
    const lat = parseFloat(latValue);
    const lng = parseFloat(lngValue);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng, ...extra } : null;
};

// The booking InfoWindow always renders above its anchor, so anything north of the pickup —
// the drop pins and the route line between them — can end up hidden behind it. These bounds
// describe the popup footprint used to detect that overlap.
const BOOKING_INFO_WINDOW_WIDTH = 400;
const BOOKING_INFO_WINDOW_HEIGHT = 360;
const BOOKING_INFO_WINDOW_MARGIN = 28;
// The popup's tail tip rests on the anchor, so the route always touches it there. Keeping the
// tested rectangle just above the tip stops that unavoidable contact from counting as overlap.
const BOOKING_INFO_WINDOW_TAIL_GAP = 6;

const orientation = (a, b, c) => Math.sign((b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y));

const segmentsCross = (p1, p2, p3, p4) => {
    const o1 = orientation(p1, p2, p3);
    const o2 = orientation(p1, p2, p4);
    const o3 = orientation(p3, p4, p1);
    const o4 = orientation(p3, p4, p2);
    return o1 !== o2 && o3 !== o4;
};

const segmentHitsRect = (p1, p2, rect) => {
    if (Math.max(p1.x, p2.x) < rect.left || Math.min(p1.x, p2.x) > rect.right) return false;
    if (Math.max(p1.y, p2.y) < rect.top || Math.min(p1.y, p2.y) > rect.bottom) return false;
    const isInside = (p) => p.x >= rect.left && p.x <= rect.right && p.y >= rect.top && p.y <= rect.bottom;
    if (isInside(p1) || isInside(p2)) return true;
    const corners = [
        { x: rect.left, y: rect.top },
        { x: rect.right, y: rect.top },
        { x: rect.right, y: rect.bottom },
        { x: rect.left, y: rect.bottom },
    ];
    return corners.some((corner, index) => segmentsCross(p1, p2, corner, corners[(index + 1) % 4]));
};

// Finds the smallest offset that keeps the popup clear of the whole route corridor, not just
// its endpoints. Shifting past the route's own extent always clears it, so a candidate exists.
const getBookingInfoWindowOffset = (projection, routePoints) => {
    if (!projection || routePoints.length < 2 || typeof window.google === 'undefined') return null;
    const pixels = routePoints.map((point) => projection.fromLatLngToContainerPixel(
        new window.google.maps.LatLng(point.lat, point.lng)
    ));
    if (pixels.some((pixel) => !pixel)) return null;

    const origin = pixels[0];
    const relative = pixels.map((pixel) => ({ x: pixel.x - origin.x, y: pixel.y - origin.y }));
    const segments = relative.slice(1).map((point, index) => [relative[index], point]);

    const halfWidth = BOOKING_INFO_WINDOW_WIDTH / 2;
    const clearance = BOOKING_INFO_WINDOW_MARGIN;
    const clearsRoute = ([offsetX, offsetY]) => {
        const rect = {
            left: offsetX - halfWidth - clearance,
            right: offsetX + halfWidth + clearance,
            top: offsetY - BOOKING_INFO_WINDOW_HEIGHT - clearance,
            bottom: offsetY - BOOKING_INFO_WINDOW_TAIL_GAP,
        };
        return !segments.some(([from, to]) => segmentHitsRect(from, to, rect));
    };

    const xs = relative.map((point) => point.x);
    const westOfRoute = Math.min(...xs) - clearance * 2 - halfWidth;
    const eastOfRoute = Math.max(...xs) + clearance * 2 + halfWidth;
    const belowPickup = BOOKING_INFO_WINDOW_HEIGHT + clearance * 2;
    const candidates = [
        [0, 0],
        [westOfRoute, 0],
        [eastOfRoute, 0],
        [0, belowPickup],
    ].sort((a, b) => Math.hypot(a[0], a[1]) - Math.hypot(b[0], b[1]));

    const chosen = candidates.find(clearsRoute) || [westOfRoute, 0];
    return new window.google.maps.Size(Math.round(chosen[0]), Math.round(chosen[1]));
};

const getBookingRoutePoints = (booking) => {
    if (!booking) return [];
    const pickup = toMapPoint(
        booking.pickup_lat,
        booking.pickup_lng || booking.pickup_lon,
        { type: 'pickup', address: booking.pickup_address }
    );
    let drops = Array.isArray(booking.drop_points) ? booking.drop_points : [];
    drops = drops
        .map((drop, index) => toMapPoint(
            drop.lat ?? drop.latitude,
            drop.lng ?? drop.longitude,
            { type: 'drop', index: index + 1, address: drop.address }
        ))
        .filter(Boolean);
    if (drops.length === 0) {
        const fallbackDrop = toMapPoint(
            booking.drop_lat,
            booking.drop_lng || booking.drop_lon,
            { type: 'drop', index: 1, address: booking.drop_address }
        );
        if (fallbackDrop) drops = [fallbackDrop];
    }
    return [pickup, ...drops].filter(Boolean);
};

// Transporter-owned drivers can arrive more than once in the live feed (one driver, many vehicles),
// so any co-located list must be collapsed per driver+vehicle before it is shown.
const dedupeDriverEntries = (list) => {
    const seen = new Set();
    const out = [];
    (list || []).forEach((d) => {
        if (!d) return;
        const key = `${String(d.driver_id ?? d.id ?? '')}::${String(d.vehicle_id ?? d.vehicle_registration ?? '')}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(d);
    });
    return out;
};

const getVehicleClassNum = (driver) => {
    const vc = String(driver?.vehicle_class || '');
    const m = vc.match(/class\s*(\d+)/i);
    if (m) return parseInt(m[1], 10);
    if (/^\d+$/.test(vc.trim())) return parseInt(vc.trim(), 10);
    return null;
};



const groupDriversByClass = (drivers) => {
    const map = new Map();
    drivers.forEach((d) => {
        const n = getVehicleClassNum(d);
        const key = n != null ? n : 0;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(d);
    });
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
};

const CLUSTER_BREAKDOWN_MIN_ZOOM = 0;
const CLUSTER_BREAKDOWN_MAX_ZOOM = 14;

const METERS_PER_DEG_LAT = 111320;

const offsetLatLngByMeters = (lat, lng, eastMeters, northMeters) => {
    const latRad = (lat * Math.PI) / 180;
    return {
        lat: lat + northMeters / METERS_PER_DEG_LAT,
        lng: lng + eastMeters / (METERS_PER_DEG_LAT * Math.cos(latRad)),
    };
};

/** Spread markers at the same lat/lng so booking pins and trucks do not stack. */
const offsetPositionForSlot = (lat, lng, slotIndex, totalSlots, zoom) => {
    if (totalSlots <= 1) return { lat, lng };

    // Use a small constant distance (18 meters) so markers stay at their real location and do not drift when zoomed out
    const spreadMeters = 18;

    if (totalSlots === 2) {
        const east = slotIndex === 0 ? -spreadMeters : spreadMeters;
        return offsetLatLngByMeters(lat, lng, east, 0);
    }

    const angle = (2 * Math.PI * slotIndex) / totalSlots - Math.PI / 2;
    const east = spreadMeters * Math.sin(angle);
    const north = spreadMeters * Math.cos(angle);
    return offsetLatLngByMeters(lat, lng, east, north);
};

const getBookingEffectiveStatus = (booking) => {
    let effectiveStatus = booking.state;
    if (booking.is_unassigned) {
        effectiveStatus = 'Unassigned';
    } else if (booking.escalation) {
        effectiveStatus = 'Delayed';
    } else if (booking.state === 'Pending') {
        const now = new Date();
        const pickupTime = new Date(booking.pickup_time);
        if (pickupTime < now) {
            const isToday = pickupTime.toDateString() === now.toDateString();
            effectiveStatus = isToday ? 'Overdue' : 'Unassigned';
        }
    }
    return effectiveStatus;
};

const buildMarkerOffsetMap = ({
    drivers,
    bookings,
    zoom,
    classFilterIds = null,
    showCompletedBookings = false,
    selectedBooking = null,
    showCriticalOnly = false,
}) => {
    const positions = new Map();
    const shouldOffset = zoom >= 10;
    const groups = new Map();

    const pushItem = (type, id, lat, lng, name = '') => {
        if (isNaN(lat) || isNaN(lng)) return;
        const key = locationKey(lat, lng);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ type, id: String(id), lat, lng, name });
    };

    bookings.forEach((booking) => {
        const id = getBookingId(booking);
        if (!id) return;
        if (
            booking.state === 'Completed'
            && !showCompletedBookings
            && (!selectedBooking || getBookingId(selectedBooking) !== id)
        ) {
            return;
        }

        const effectiveStatus = getBookingEffectiveStatus(booking);
        const isUrgent = effectiveStatus === 'Unassigned'
            || effectiveStatus === 'Delayed'
            || effectiveStatus === 'Overdue';
        if (showCriticalOnly && !isUrgent) return;

        const lat = parseFloat(booking.pickup_lat);
        const lng = parseFloat(booking.pickup_lng || booking.pickup_lon);
        pushItem('booking', id, lat, lng);
    });

    drivers.forEach((driver) => {
        const id = String(driver.driver_id || driver.id);
        if (!id) return;
        if (classFilterIds && !classFilterIds.has(id)) return;
        const lat = parseFloat(driver.current_lat);
        const lng = parseFloat(driver.current_lng);
        pushItem('driver', id, lat, lng, driver.driver_name || '');
    });

    groups.forEach((items) => {
        if (!shouldOffset || items.length <= 1) {
            items.forEach((it) => {
                positions.set(`${it.type}:${it.id}`, { lat: it.lat, lng: it.lng });
            });
            return;
        }

        items.sort((a, b) => {
            if (a.type !== b.type) return a.type === 'booking' ? -1 : 1;
            if (a.type === 'booking') return a.id.localeCompare(b.id);
            return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });

        items.forEach((it, idx) => {
            positions.set(
                `${it.type}:${it.id}`,
                offsetPositionForSlot(it.lat, it.lng, idx, items.length, zoom)
            );
        });
    });

    return positions;
};

const getMarkerDisplayPosition = (offsetMap, type, id, trueLat, trueLng) => {
    const key = `${type}:${String(id)}`;
    return offsetMap.get(key) || { lat: trueLat, lng: trueLng };
};



const OpsMapView = ({ drivers, bookings, trips = [], selectedDriver, selectedBooking, onSelectDriver, onSelectBooking, onClusterDriversSelect, onViewVehicleDetails, mapViewport, onViewportChange, showServiceZone, showHotzones, showRealtimeHotzones, showHistoricalHotzones, showLeadHeatmap = false, leadDestinations = [], selectedLeadDestination = null, onSelectLeadDestination, showCriticalOnly, showCompletedBookings, districtMapping, serviceZoneRefreshKey = 0, cityHighlights = [], onClearCityHighlights, pincodeAnalysis = null, skipInitialPincodeFit = false, focusSingleDriver = false, onMapReady }) => {
    const { isLoaded, loadError } = useGoogleMaps();

    const [map, setMap] = useState(null);
    const [clickedCityPincode, setClickedCityPincode] = useState(null);

    const hasFittedBounds = useRef(false);
    const isInitialLoadRef = useRef(true);
    const [serviceZones, setServiceZones] = useState([]);
    const [hotzones, setHotzones] = useState([]);
    const [selectedHotzone, setSelectedHotzone] = useState(null);
    const [infoWindowAnchor, setInfoWindowAnchor] = useState(null);
    // All bookings at the clicked marker's location (for scrollable InfoWindow)
    const [selectedLocationBookings, setSelectedLocationBookings] = useState([]);
    // All drivers at the clicked marker's location (for scrollable InfoWindow)
    const [selectedLocationDrivers, setSelectedLocationDrivers] = useState([]);
    const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);

    // Ref to always access latest bookings in marker click handlers (avoids stale closure)
    const bookingsRef = useRef(bookings);
    bookingsRef.current = bookings;
    // Ref to always access latest drivers in marker click handlers (avoids stale closure)
    const driversRef = useRef(drivers);
    driversRef.current = drivers;
    const onClusterDriversSelectRef = useRef(onClusterDriversSelect);
    onClusterDriversSelectRef.current = onClusterDriversSelect;
    const onSelectDriverRef = useRef(onSelectDriver);
    onSelectDriverRef.current = onSelectDriver;
    const onSelectBookingRef = useRef(onSelectBooking);
    onSelectBookingRef.current = onSelectBooking;

    const applyDriverMarkerVisibility = useCallback((showMarkers) => {
        driverMarkerPool.current.forEach(({ marker }, id) => {
            if (!marker) return;
            const sid = String(id);
            let visible = showMarkers;
            if (classFilterDriverIdsRef.current) {
                visible = showMarkers && classFilterDriverIdsRef.current.has(sid);
            }
            // A marker absorbed into a cluster bubble must stay hidden, otherwise the
            // truck icon renders alongside the count bubble it is supposed to be inside.
            if (visible && clusteredDriverIdsRef.current.has(sid)) visible = false;
            if (marker.getVisible() !== visible) marker.setVisible(visible);
        });
    }, []);

    const clearClassFilter = useCallback((clearSelectionUi = false) => {
        classFilterDriverIdsRef.current = null;
        applyDriverMarkerVisibility(currentZoom.current >= CLUSTER_BREAKDOWN_MIN_ZOOM);
        if (clearSelectionUi) {
            selectedDriverRef.current = null;
            setInfoWindowAnchor(null);
            setSelectedLocationDrivers([]);
            onSelectDriverRef.current?.(null);
            onClusterDriversSelectRef.current?.(null);
        }
        rebuildClusterBreakdownRef.current();
        rebuildPixelClustersRef.current();
    }, [map, applyDriverMarkerVisibility]);

    const applyClassFilter = useCallback((drivers) => {
        if (!map || !drivers?.length) return;
        const ids = new Set(drivers.map((d) => String(d.driver_id || d.id)));
        classFilterDriverIdsRef.current = ids;
        applyDriverMarkerVisibility(true);
        rebuildPixelClustersRef.current();

        const bounds = new window.google.maps.LatLngBounds();
        drivers.forEach((d) => {
            const lat = parseFloat(d.current_lat);
            const lng = parseFloat(d.current_lng);
            if (!isNaN(lat) && !isNaN(lng)) bounds.extend({ lat, lng });
        });
        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, { top: 90, bottom: 90, left: 90, right: 90 });
            window.google.maps.event.addListenerOnce(map, 'idle', () => {
                if (map.getZoom() > 16) map.setZoom(16);
            });
        }

        drivers.sort((a, b) =>
            (a.driver_name || '').localeCompare(b.driver_name || '', undefined, { sensitivity: 'base' })
        );
        setSelectedLocationDrivers(drivers);
        setSelectedLocationBookings([]);
        onSelectBookingRef.current?.(null);
        onSelectDriverRef.current?.(null);
        onClusterDriversSelectRef.current?.(drivers);
        rebuildClusterBreakdownRef.current();
    }, [map, applyDriverMarkerVisibility]);

    // Fetch serviceable zones once on mount
    useEffect(() => {
        const fetchServiceZones = async () => {
            try {
                const baseUrl = config.dispatchApiBase || 'http://localhost:8001/api';
                const response = await fetch(`${baseUrl}/zones/serviceable`);
                const data = await response.json();
                if (data.success && data.polygons) {
                    setServiceZones(data.polygons);
                }
            } catch (error) {
                console.error('Error fetching service zones:', error);
            }
        };
        fetchServiceZones();
    }, [serviceZoneRefreshKey]);

    // Fetch hotzones periodically
    useEffect(() => {
        const fetchHotzones = async () => {
            try {
                const baseUrl = config.dispatchApiBase || 'http://localhost:8001/api';
                const response = await fetch(`${baseUrl}/ops/hotzones`);
                const data = await response.json();
                if (data.success && data.hotzones) {
                    setHotzones(data.hotzones);
                }
            } catch (error) {
                console.error('Error fetching hotzones:', error);
            }
        };

        // Fetch immediately, then every 60s
        fetchHotzones();
        const intervalId = setInterval(fetchHotzones, 60000);
        return () => clearInterval(intervalId);
    }, []);

    // Track which booking we've already auto-zoomed to, to avoid fighting with user panning or data refreshes
    const lastZoomedBookingId = useRef(null);
    const lastFittedCitiesRef = useRef('');

    // Pan map to selected driver or booking when selection changes (e.g. from sidebar click)
    React.useEffect(() => {
        if (!map) return;
        if (selectedDriver) {
            if (skipNextDriverMapZoomRef.current) {
                skipNextDriverMapZoomRef.current = false;
                lastZoomedBookingId.current = null;
                return;
            }
            const lat = parseFloat(selectedDriver.current_lat);
            const lng = parseFloat(selectedDriver.current_lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                map.panTo({ lat, lng });
                // Ensure zoom is at least 15 to see markers clearly
                if (map.getZoom() < 15) map.setZoom(15);
                lastZoomedBookingId.current = null; // Clear booking zoom lock
            }
        } else if (selectedBooking) {
            const bookingId = getBookingId(selectedBooking);

            // Only auto-zoom if this is a NEW selection to prevent fighting user's manual pans/zooms
            if (lastZoomedBookingId.current === bookingId) return;
            lastZoomedBookingId.current = bookingId;

            const routePoints = getBookingRoutePoints(selectedBooking);
            if (routePoints.length > 1) {
                const bounds = new window.google.maps.LatLngBounds();
                routePoints.forEach((point) => bounds.extend(point));
                map.fitBounds(bounds, { top: 110, bottom: 110, left: 110, right: 110 });
                window.google.maps.event.addListenerOnce(map, 'idle', () => {
                    if (map.getZoom() > 16) map.setZoom(16);
                });
            } else if (routePoints.length === 1) {
                map.panTo(routePoints[0]);
                if (map.getZoom() < 16) map.setZoom(16);
            }
        } else {
            lastZoomedBookingId.current = null;
        }
    }, [map, selectedDriver, selectedBooking]);

    // Pan map or fit bounds for pincodeAnalysis
    React.useEffect(() => {
        if (!map || !pincodeAnalysis) {
            lastFittedCitiesRef.current = '';
            return;
        }

        const citiesKey = pincodeAnalysis.city || '';
        // If we already fitted bounds for these cities/pincode, don't do it again!
        if (lastFittedCitiesRef.current === citiesKey) return;
        lastFittedCitiesRef.current = citiesKey;

        // Skip fitting bounds only on the initial load/mount when skipInitialPincodeFit is active
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            if (skipInitialPincodeFit) {
                return;
            }
        }

        if (pincodeAnalysis.is_city_wide) {
            const bounds = new window.google.maps.LatLngBounds();
            let hasPoints = false;

            if (pincodeAnalysis.pincodes && pincodeAnalysis.pincodes.length > 0) {
                pincodeAnalysis.pincodes.forEach(p => {
                    if (p.center && typeof p.center.lat === 'number' && typeof p.center.lng === 'number') {
                        bounds.extend(p.center);
                        hasPoints = true;
                    }
                });
            }

            if (hasPoints) {
                map.fitBounds(bounds);
                window.google.maps.event.addListenerOnce(map, 'idle', () => {
                    if (map.getZoom() > 13) map.setZoom(13);
                });
            }
        } else if (pincodeAnalysis.center) {
            const center = pincodeAnalysis.center;
            if (typeof center.lat === 'number' && typeof center.lng === 'number') {
                map.panTo(center);
                map.setZoom(13); // Zoom level 13 is good for viewing a pincode area
            }
        }
    }, [map, pincodeAnalysis, skipInitialPincodeFit]);

    // Reset clicked city-wide pincode marker when analysis changes
    React.useEffect(() => {
        setClickedCityPincode(null);
    }, [pincodeAnalysis]);

    // When a driver is selected (incl. sidebar after class filter), show map popup + anchor to marker
    useEffect(() => {
        if (!selectedDriver) {
            setInfoWindowAnchor(null);
            selectedDriverRef.current = null;
            return;
        }

        selectedDriverRef.current = selectedDriver;
        // Already zoomed from class filter — don't pan again on sidebar pick
        if (classFilterDriverIdsRef.current?.size) {
            skipNextDriverMapZoomRef.current = true;
        }

        const lat = parseFloat(selectedDriver.current_lat);
        const lng = parseFloat(selectedDriver.current_lng);
        // When coming from a cluster legend click (focusSingleDriver=true), show only the
        // selected vehicle in the InfoWindow — don't expand to all co-located drivers.
        let panelDrivers = [selectedDriver];
        if (!focusSingleDriver && !isNaN(lat) && !isNaN(lng)) {
            const key = locationKey(lat, lng);
            const coLocated = dedupeDriverEntries(driversRef.current.filter((d) => {
                const dLat = parseFloat(d.current_lat);
                const dLng = parseFloat(d.current_lng);
                return locationKey(dLat, dLng) === key;
            }));
            if (coLocated.length > 0) {
                panelDrivers = coLocated;
            }
        }
        if (classFilterDriverIdsRef.current?.size) {
            const filtered = panelDrivers.filter((d) =>
                classFilterDriverIdsRef.current.has(String(d.driver_id || d.id))
            );
            panelDrivers = filtered.length > 0 ? filtered : [selectedDriver];
        }
        setSelectedLocationDrivers(panelDrivers);
        setSelectedLocationBookings([]);

        const driverId = selectedDriver.driver_id || selectedDriver.id;
        let cancelled = false;
        const attachInfoWindowAnchor = (attempt = 0) => {
            if (cancelled) return;
            const entry = driverMarkerPool.current.get(driverId);
            const marker = entry?.marker;
            if (marker?.getMap?.()) {
                setInfoWindowAnchor(marker);
                return;
            }
            if (attempt < 8) {
                window.setTimeout(() => attachInfoWindowAnchor(attempt + 1), 150);
            } else {
                setInfoWindowAnchor(null);
            }
        };
        attachInfoWindowAnchor();

        return () => {
            cancelled = true;
        };
    }, [selectedDriver]);

    // Update local location-based selection state when props change (from sidebar click)
    useEffect(() => {
        if (selectedDriver) {
            // Driver panel + InfoWindow list handled in effect above
        } else if (selectedBooking) {
            const lat = parseFloat(selectedBooking.pickup_lat);
            const lng = parseFloat(selectedBooking.pickup_lng || selectedBooking.pickup_lon);
            if (!isNaN(lat) && !isNaN(lng)) {
                const key = locationKey(lat, lng);
                const urgencyScore = (b) => {
                    if (b.is_unassigned) return 0;
                    const st = b.state;
                    if (st === 'Delayed') return 1;
                    if (st === 'Pending') return 2;
                    if (st === 'Assigned') return 3;
                    return 4;
                };
                const coLocated = bookingsRef.current
                    .filter(b => {
                        const bLat = parseFloat(b.pickup_lat);
                        const bLng = parseFloat(b.pickup_lng || b.pickup_lon);
                        return locationKey(bLat, bLng) === key;
                    })
                    .sort((a, b) => urgencyScore(a) - urgencyScore(b));
                setSelectedLocationBookings(coLocated.length > 0 ? coLocated : [selectedBooking]);
                setSelectedLocationDrivers([]);
            }
        } else {
            // No selection - clear location states (but keep logic if InfoWindow should stay open?)
            // Usually, clicking the map background calls onSelect(null), so this is fine.
            setSelectedLocationDrivers([]);
            setSelectedLocationBookings([]);
        }
    }, [selectedDriver, selectedBooking, bookings]);

    const onLoad = useCallback(function callback(mapInstance) {
        setMap(mapInstance);
        if (onMapReady) onMapReady(mapInstance);
    }, [onMapReady]);

    const onUnmount = useCallback(function callback() {
        setMap(null);
    }, []);

    // ---- Marker diffing: maintain pools instead of destroy/rebuild every poll ----
    const driverMarkerPool = useRef(new Map()); // driver_id → { marker, listener }
    const bookingMarkerPool = useRef(new Map()); // booking.id → { marker, listener, pulseDiv }
    const pixelClusterOverlayPool = useRef([]); // PixelClusterOverlay instances (visual cluster bubbles)
    const pixelClusterProjectionRef = useRef(null); // Updated by OverlayView when projection is ready
    const clusteredDriverIdsRef = useRef(new Set()); // String(driver_id) of markers currently absorbed into a cluster bubble
    const rebuildPixelClustersRef = useRef(() => { });
    const currentZoom = useRef(11);
    const [zoom, setZoom] = useState(11); // Reactive zoom for conditional rendering
    const prevUrgencyRef = useRef(new Map()); // booking.id → was urgent last render (for bounce-on-entry)
    const cityHighlightMarkersRef = useRef([]); // plain Markers for City Navigation selections (distinct visual + no clustering)
    const lastCityHighlightsKeyRef = useRef('');
    const classFilterDriverIdsRef = useRef(null);
    const rebuildClusterBreakdownRef = useRef(() => { });
    const skipNextDriverMapZoomRef = useRef(false);
    const selectedDriverRef = useRef(selectedDriver);
    selectedDriverRef.current = selectedDriver;
    // eslint-disable-next-line no-unused-vars
    const hotzonePolygonPool = useRef(new Map()); // hotzone_id → google.maps.Polygon (Phase 2: polygon caching)



    // Inject / refresh map overlay styles
    useEffect(() => {
        let style = document.getElementById('ops-pulse-style');
        if (!style) {
            style = document.createElement('style');
            style.id = 'ops-pulse-style';
            document.head.appendChild(style);
        }
        style.textContent = `
            @keyframes ops-pulse-ring {
                0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.7; }
                100% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
            }
            .ops-pulse-ring {
                position: absolute;
                width: 28px;
                height: 28px;
                border-radius: 50%;
                animation: ops-pulse-ring 2.5s ease-out infinite;
                pointer-events: none;
            }
            .ops-cluster-class-btn {
                font-family: 'Google Sans', Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                font-size: 11px;
                font-weight: 500;
                letter-spacing: 0.01em;
                padding: 5px 10px;
                border-radius: 8px;
                border: 1px solid rgba(255, 255, 255, 0.45);
                background: rgba(255, 255, 255, 0.58);
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                color: #1e3a2f;
                cursor: pointer;
                box-shadow: 0 1px 2px rgba(60, 64, 67, 0.12), 0 2px 8px rgba(60, 64, 67, 0.18);
                white-space: nowrap;
                line-height: 1.2;
                transition: background 0.15s ease, box-shadow 0.15s ease;
            }
            .ops-cluster-class-btn:hover {
                background: rgba(255, 255, 255, 0.72);
                box-shadow: 0 2px 4px rgba(60, 64, 67, 0.16), 0 4px 12px rgba(60, 64, 67, 0.2);
            }
            .ops-cluster-class-btn.open {
                border-radius: 8px 8px 0 0;
                border-bottom: 1px solid rgba(5, 150, 105, 0.12);
                background: rgba(255, 255, 255, 0.68);
            }
            .ops-cluster-class-menu {
                position: absolute;
                left: 50%;
                transform: translateX(-50%);
                top: 100%;
                min-width: 140px;
                background: rgba(255, 255, 255, 0.65);
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                border: 1px solid rgba(255, 255, 255, 0.45);
                border-top: none;
                border-radius: 0 0 8px 8px;
                box-shadow: 0 2px 8px rgba(60, 64, 67, 0.18);
                z-index: 101;
                overflow: hidden;
            }
            .ops-cluster-class-row {
                display: block;
                width: 100%;
                text-align: left;
                padding: 7px 12px;
                font-size: 11px;
                font-weight: 500;
                color: #3c4043;
                background: transparent;
                border: none;
                border-bottom: 1px solid rgba(60, 64, 67, 0.08);
                cursor: pointer;
                transition: background 0.12s ease;
            }
            .ops-cluster-class-row:last-child { border-bottom: none; }
            .ops-cluster-class-row:hover {
                background: rgba(16, 185, 129, 0.12);
                color: #047857;
            }
            .ops-cluster-class-wrap {
                position: absolute;
                pointer-events: none;
                z-index: 100;
                transform: translate(-50%, calc(-100% - 6px));
                filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.08));
                opacity: 0;
                transition: opacity 0.15s ease;
                padding-bottom: 18px;
                margin-bottom: -18px;
            }
            .ops-cluster-class-wrap.ops-cluster-class-wrap--show {
                opacity: 1;
                pointer-events: auto;
            }
        `;
    }, []);

    // Track zoom level for zoom-based rendering
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('zoom_changed', () => {
            const z = map.getZoom();
            currentZoom.current = z;
            setZoom(z);
        });
        return () => window.google.maps.event.removeListener(listener);
    }, [map]);

    // =========================
    // Global handleDriverFocus for Syncing Table clicks
    // =========================
    useEffect(() => {
        window.handleDriverFocus = (trip) => {
            if (!map) return;

            let targetDriverId = trip.driver_id;
            let targetDriver = null;

            if (targetDriverId) {
                targetDriver = driversRef.current.find(d => (d.driver_id || d.id) === targetDriverId);
            }
            if (!targetDriver && trip.driver_name) {
                targetDriver = driversRef.current.find(d => d.driver_name === trip.driver_name);
            }

            if (targetDriver) {
                targetDriverId = targetDriver.driver_id || targetDriver.id;
            } else {
                targetDriverId = trip.driver_id || trip.driver_name;
            }

            const focus = (retryCount = 0) => {
                const poolItem = driverMarkerPool.current.get(targetDriverId);
                const marker = poolItem ? poolItem.marker : null;

                if (marker) {
                    map.panTo(marker.getPosition());
                    map.setZoom(16);
                    window.google.maps.event.trigger(marker, 'click');
                } else if (retryCount < 3) {
                    setTimeout(() => focus(retryCount + 1), 400);
                }
            };

            focus();
        };

        return () => {
            delete window.handleDriverFocus;
        };
    }, [map]);

    // =========================
    // Tier 1: Toggle cluster/marker visibility based on zoom
    // =========================
    useEffect(() => {
        const showMarkers = zoom >= CLUSTER_BREAKDOWN_MIN_ZOOM;
        if (zoom <= CLUSTER_BREAKDOWN_MAX_ZOOM && classFilterDriverIdsRef.current) {
            clearClassFilter(true);
        }
        applyDriverMarkerVisibility(showMarkers);
        // Toggle booking markers + pulse overlays
        bookingMarkerPool.current.forEach(({ marker, pulseDiv }) => {
            marker.setVisible(showMarkers);
            if (pulseDiv) {
                if (!showMarkers && pulseDiv.getMap()) pulseDiv.setMap(null);
                else if (showMarkers && !pulseDiv.getMap()) pulseDiv.setMap(map);
            }
        });
        // Trigger pixel cluster rebuild on zoom change
        rebuildPixelClustersRef.current();
    }, [zoom, map, applyDriverMarkerVisibility, clearClassFilter]);



    // =========================
    // Pixel Cluster Setup — creates projection hook + bubble overlays on idle/zoom
    // =========================
    useEffect(() => {
        if (!map || typeof window.google === 'undefined') return;

        // ----- Pixel Cluster Overlay: renders green circle bubbles -----
        class PixelClusterBubble extends window.google.maps.OverlayView {
            constructor(lat, lng, count, driverIds, onClickCb, hasTransporter = false) {
                super();
                this._lat = lat;
                this._lng = lng;
                this._count = count;
                this._driverIds = driverIds;
                this._onClick = onClickCb;
                this._position = new window.google.maps.LatLng(lat, lng);
                this._hasTransporter = hasTransporter;
                this.div = null;
            }
            onAdd() {
                const size = this._count >= 10 ? 38 : this._count >= 5 ? 34 : 30;
                const bubbleColor = this._hasTransporter ? 'rgba(219,39,119,0.85)' : 'rgba(16,185,129,0.7)';
                const shadowColor = this._hasTransporter ? 'rgba(219,39,119,0.4)' : 'rgba(16,185,129,0.4)';
                const hoverShadowColor = this._hasTransporter ? 'rgba(219,39,119,0.6)' : 'rgba(16,185,129,0.6)';
                const div = document.createElement('div');
                div.style.cssText = `
                    position:absolute;
                    width:${size}px; height:${size}px;
                    border-radius:50%;
                    background:${bubbleColor};
                    border:none;
                    box-shadow:0 2px 6px ${shadowColor};
                    display:flex; align-items:center; justify-content:center;
                    font-family:'Google Sans',Roboto,sans-serif;
                    font-size:${this._count >= 10 ? 13 : 12}px;
                    font-weight:700;
                    color:#fff;
                    cursor:pointer;
                    transform:translate(-50%,-50%);
                    z-index:300;
                    transition:transform 0.12s ease,box-shadow 0.12s ease;
                `;
                div.textContent = String(this._count);
                div.addEventListener('mouseenter', () => {
                    div.style.transform = 'translate(-50%,-50%) scale(1.12)';
                    div.style.boxShadow = `0 4px 12px ${hoverShadowColor}`;
                });
                div.addEventListener('mouseleave', () => {
                    div.style.transform = 'translate(-50%,-50%)';
                    div.style.boxShadow = `0 2px 6px ${shadowColor}`;
                });
                div.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this._onClick(this._driverIds, this._position);
                });
                this.div = div;
                this.getPanes().floatPane.appendChild(div);
            }
            draw() {
                if (!this.div) return;
                const proj = this.getProjection();
                if (!proj) return;
                const p = proj.fromLatLngToDivPixel(this._position);
                if (p) {
                    this.div.style.left = p.x + 'px';
                    this.div.style.top = p.y + 'px';
                }
                // Expose projection for pixel cluster engine
                pixelClusterProjectionRef.current = proj;
            }
            onRemove() {
                if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
                this.div = null;
            }
        }

        // ----- Projection Hook OverlayView (invisible, just exposes the projection) -----
        class ProjectionHook extends window.google.maps.OverlayView {
            onAdd() { }
            draw() {
                pixelClusterProjectionRef.current = this.getProjection();
            }
            onRemove() { }
        }
        const hook = new ProjectionHook();
        hook.setMap(map);

        const handleClusterClick = (driverIds, centroidLatLng) => {
            const clusterIds = new Set(driverIds.map(id => String(id)));
            const driversInCluster = dedupeDriverEntries(
                driversRef.current.filter(drv => clusterIds.has(String(drv.driver_id || drv.id)))
            );
            driversInCluster.sort((a, b) =>
                (a.driver_name || '').localeCompare(b.driver_name || '', undefined, { sensitivity: 'base' })
            );
            setSelectedLocationDrivers(driversInCluster);
            setSelectedLocationBookings([]);
            onSelectBookingRef.current?.(null);
            onSelectDriverRef.current?.(null);
            onClusterDriversSelectRef.current?.(driversInCluster);

            // Smooth pan + zoom (same feel as scroll-wheel zoom). Target zoom is derived from
            // the members' real span so a city-wide group does not jump to street level and
            // lose vehicles off-screen.
            const bounds = new window.google.maps.LatLngBounds();
            driverIds.forEach((id) => {
                const entry = driverMarkerPool.current.get(id)
                    || driverMarkerPool.current.get(String(id));
                const pos = entry?.marker?.getPosition();
                if (pos) bounds.extend(pos);
            });

            const currentZoom = map.getZoom() || 11;
            let targetZoom = Math.min(currentZoom + 2, CLUSTER_CLICK_MAX_ZOOM);
            if (!bounds.isEmpty()) {
                const fitted = estimateZoomForBounds(map, bounds);
                // Prefer a zoom that keeps the group on screen; still nudge in if already close.
                targetZoom = Math.min(
                    CLUSTER_CLICK_MAX_ZOOM,
                    Math.max(fitted, Math.min(currentZoom + 1, CLUSTER_CLICK_MAX_ZOOM))
                );
            }

            map.panTo(centroidLatLng);
            if (targetZoom !== currentZoom) map.setZoom(targetZoom);
        };

        const removePixelClusterOverlays = () => {
            pixelClusterOverlayPool.current.forEach(o => o.setMap(null));
            pixelClusterOverlayPool.current = [];
        };

        let rafHandle = null;
        const scheduleRebuildPixelClusters = () => {
            if (rafHandle) return; // already scheduled this frame
            rafHandle = requestAnimationFrame(() => {
                rafHandle = null;
                doRebuildPixelClusters();
            });
        };

        // Markers eligible to be drawn/clustered right now (everything, minus the class filter)
        const collectEligibleIds = () => {
            const filterIds = classFilterDriverIdsRef.current;
            const eligible = new Set();
            driverMarkerPool.current.forEach((_entry, id) => {
                const sid = String(id);
                if (!filterIds || filterIds.has(sid)) eligible.add(sid);
            });
            return eligible;
        };

        // Single source of truth for driver marker visibility: a marker is shown only when it
        // is eligible AND not absorbed into a cluster bubble. Iterating the pool (rather than
        // diffing id sets) keeps this correct regardless of numeric vs. string driver ids.
        const applyClusterVisibility = (eligible, clusteredSids) => {
            driverMarkerPool.current.forEach((entry, id) => {
                const marker = entry?.marker;
                if (!marker) return;
                const sid = String(id);
                const shouldShow = eligible.has(sid) && !clusteredSids.has(sid);
                if (marker.getVisible() !== shouldShow) marker.setVisible(shouldShow);
            });
            clusteredDriverIdsRef.current = clusteredSids;
        };

        const doRebuildPixelClusters = () => {
            removePixelClusterOverlays();
            if (driverMarkerPool.current.size === 0) return;

            const eligible = collectEligibleIds();
            const proj = pixelClusterProjectionRef.current;

            let clusters = [];
            let newClusteredIds = new Set();
            if (proj && eligible.size >= 2) {
                ({ clusters, clusteredKeys: newClusteredIds } = computePixelClusters(
                    driverMarkerPool.current,
                    proj,
                    PIXEL_CLUSTER_RADIUS,
                    eligible
                ));
            }

            applyClusterVisibility(eligible, newClusteredIds);

            clusters.forEach(({ keys, centroidLat, centroidLng, count }) => {
                // driversRef (not the effect's captured `drivers`) — this effect only re-runs on
                // map changes, so the closure copy is stale and would keep every bubble green.
                const hasTransporter = keys.some(id => {
                    const d = driversRef.current.find(drv => String(drv.driver_id || drv.id) === String(id));
                    return d?.is_transporter;
                });
                const bubble = new PixelClusterBubble(centroidLat, centroidLng, count, keys, handleClusterClick, hasTransporter);
                bubble.setMap(map);
                pixelClusterOverlayPool.current.push(bubble);
            });
        };

        rebuildPixelClustersRef.current = scheduleRebuildPixelClusters;

        const idleListener = map.addListener('idle', scheduleRebuildPixelClusters);
        // Keep bubbles collapsed during the zoom animation; re-cluster once the map settles,
        // so the trucks only appear when the new zoom level actually pulls them apart.
        const zoomListener = map.addListener('zoom_changed', scheduleRebuildPixelClusters);

        return () => {
            window.google.maps.event.removeListener(idleListener);
            window.google.maps.event.removeListener(zoomListener);
            removePixelClusterOverlays();
            hook.setMap(null);
            rebuildPixelClustersRef.current = () => { };
            clusteredDriverIdsRef.current = new Set();
            // Full cleanup on unmount
            driverMarkerPool.current.forEach(({ marker, listener }) => {
                window.google.maps.event.removeListener(listener);
                marker.setMap(null);
            });
            bookingMarkerPool.current.forEach(({ marker, listener, pulseDiv }) => {
                window.google.maps.event.removeListener(listener);
                marker.setMap(null);
                if (pulseDiv) pulseDiv.setMap(null);
            });
            driverMarkerPool.current.clear();
            bookingMarkerPool.current.clear();
            // City navigation highlights
            cityHighlightMarkersRef.current.forEach((m) => { try { m.setMap(null); } catch (_) { } });
            cityHighlightMarkersRef.current = [];
            lastCityHighlightsKeyRef.current = '';
        };
    }, [map]);

    // Class breakdown (formerly per-clusterer): now a no-op since clustering is pixel-based.
    // Kept as a stub so other call-sites using rebuildClusterBreakdownRef don't error.
    useEffect(() => {
        rebuildClusterBreakdownRef.current = () => { };
        return () => {
            rebuildClusterBreakdownRef.current = () => { };
        };
    }, [map]);


    // City Navigation highlights (from CityExplorer in main dashboard).
    // Renders as distinct indigo location pins (non-clustered), auto-pans the map, and keeps them visually separate from live trucks/bookings.
    useEffect(() => {
        if (!map || typeof window.google === 'undefined') return;

        // Clean previous
        cityHighlightMarkersRef.current.forEach((m) => { try { m.setMap(null); } catch (_) { } });
        cityHighlightMarkersRef.current = [];

        const highlights = Array.isArray(cityHighlights) ? cityHighlights : [];
        const key = JSON.stringify(highlights.map(h => ({ lat: h.lat, lng: h.lng, label: h.label })));
        if (key === lastCityHighlightsKeyRef.current && highlights.length === 0) {
            return;
        }
        lastCityHighlightsKeyRef.current = key;

        if (highlights.length === 0) {
            return;
        }

        highlights.forEach((h, idx) => {
            if (!h || typeof h.lat !== 'number' || typeof h.lng !== 'number') return;

            const marker = new window.google.maps.Marker({
                position: { lat: h.lat, lng: h.lng },
                map,
                title: h.label || (h.pincode ? `Pincode ${h.pincode}` : (h.city || 'Selected area')),
                zIndex: 920 + idx,
                icon: {
                    // Classic pin shape, indigo to signal "user-added navigation selection" (distinct from green trucks / blue bookings)
                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                    fillColor: '#6366F1',
                    fillOpacity: 0.95,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                    scale: 1.45,
                    anchor: { x: 12, y: 22 },
                },
            });

            if (h.pincode) {
                marker.setLabel({
                    text: String(h.pincode).slice(-4),
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: '700',
                });
            }

            // Click the highlight marker → clear only the highlights (optional)
            marker.addListener('click', () => {
                if (typeof onClearCityHighlights === 'function') {
                    onClearCityHighlights();
                }
            });

            cityHighlightMarkersRef.current.push(marker);
        });

        // Focus the primary selection
        if (highlights.length > 1) {
            try {
                const bounds = new window.google.maps.LatLngBounds();
                highlights.forEach((h) => {
                    if (h && typeof h.lat === 'number' && typeof h.lng === 'number') {
                        bounds.extend({ lat: h.lat, lng: h.lng });
                    }
                });
                map.fitBounds(bounds);
            } catch (_) { }
        } else if (highlights.length === 1) {
            const first = highlights[0];
            try {
                map.panTo({ lat: first.lat, lng: first.lng });
                const z = map.getZoom();
                if (!z || z < 12) map.setZoom(13);
            } catch (_) { }
        }

        return () => {
            cityHighlightMarkersRef.current.forEach((m) => { try { m.setMap(null); } catch (_) { } });
            cityHighlightMarkersRef.current = [];
        };
    }, [map, cityHighlights, onClearCityHighlights]);

    // Note: registered drivers are placed directly on the map — not clustered.

    // Diff-based driver marker updates
    useEffect(() => {
        if (!map) return;

        const mapZoom = map.getZoom() ?? currentZoom.current ?? zoom;
        const offsetMap = buildMarkerOffsetMap({
            drivers: driversRef.current,
            bookings: bookingsRef.current,
            zoom: mapZoom,
            classFilterIds: classFilterDriverIdsRef.current,
            showCompletedBookings,
            selectedBooking,
            showCriticalOnly,
        });

        const currentIds = new Set();
        drivers.forEach((driver) => {
            const id = driver.driver_id || driver.id;
            if (!id) return;
            currentIds.add(id);
            const trueLat = parseFloat(driver.current_lat);
            const trueLng = parseFloat(driver.current_lng);
            if (isNaN(trueLat) || isNaN(trueLng)) return;
            const { lat, lng } = getMarkerDisplayPosition(offsetMap, 'driver', id, trueLat, trueLng);

            let opacity = 1.0;
            let zIndex = 10;
            if (driver.driver_status === 'available') {
                zIndex = 100;
            } else if (driver.driver_status === 'active') {
                zIndex = 50;
            } else if (driver.type === 'registered' || driver.driver_status === 'registered') {
                zIndex = 20;
            } else {
                zIndex = 1;
                opacity = 0.6;
            }
            if (driver.is_transporter) {
                zIndex += 1000;
            }

            const existing = driverMarkerPool.current.get(id);
            if (existing) {
                existing.trueLat = trueLat;
                existing.trueLng = trueLng;
                // Directly set position instead of animating
                existing.marker.setPosition({ lat, lng });

                // Update icon if status, tripStatus, or zoom changed to prevent flickering and support dynamic scaling
                if (existing.status !== driver.driver_status || existing.tripStatus !== driver.current_trip_status || existing.zoom !== mapZoom || existing.isTransporter !== !!driver.is_transporter) {
                    existing.marker.setIcon(getDriverIcon(driver.driver_status, driver.current_trip_status, mapZoom, !!driver.is_transporter));
                    existing.status = driver.driver_status;
                    existing.tripStatus = driver.current_trip_status;
                    existing.zoom = mapZoom;
                    existing.isTransporter = !!driver.is_transporter;
                }

                existing.marker.setOpacity(opacity);
                existing.marker.setZIndex(zIndex);
                existing._opacity = opacity;
            } else {
                // New driver — create marker
                const marker = new window.google.maps.Marker({
                    position: { lat, lng },
                    icon: getDriverIcon(driver.driver_status, driver.current_trip_status, mapZoom, !!driver.is_transporter),
                    title: `Driver: ${driver.driver_name} | ${driver.vehicle_id || '-'}`,
                    opacity,
                    zIndex,
                });
                const listener = marker.addListener('click', () => {
                    const key = locationKey(trueLat, trueLng);

                    onSelectBookingRef.current?.(null);
                    setSelectedLocationBookings([]);

                    const coLocated = dedupeDriverEntries(driversRef.current.filter(d => {
                        const dLat = parseFloat(d.current_lat);
                        const dLng = parseFloat(d.current_lng);
                        return locationKey(dLat, dLng) === key;
                    }));
                    const latestDriver = driversRef.current.find(d => (d.driver_id || d.id) === id);
                    const panelDrivers = coLocated.length > 0
                        ? coLocated
                        : [latestDriver || driver];

                    panelDrivers.sort((a, b) =>
                        (a.driver_name || '').localeCompare(b.driver_name || '', undefined, { sensitivity: 'base' })
                    );
                    setSelectedLocationDrivers(panelDrivers);

                    // Marker tap: map InfoWindow + right sidebar, but do not zoom/pan
                    const picked = latestDriver || driver;
                    selectedDriverRef.current = picked;
                    setInfoWindowAnchor(marker);
                    skipNextDriverMapZoomRef.current = true;
                    onSelectDriverRef.current?.(picked);
                    onClusterDriversSelectRef.current?.(panelDrivers);
                    rebuildClusterBreakdownRef.current();
                });
                driverMarkerPool.current.set(id, {
                    marker,
                    listener,
                    status: driver.driver_status,
                    tripStatus: driver.current_trip_status,
                    trueLat,
                    trueLng,
                    zoom: mapZoom,
                    _opacity: opacity,
                    isTransporter: !!driver.is_transporter,
                });
                // All drivers render directly on the map; pixel cluster engine hides overlapping ones
                marker.setMap(map);
            }
        });

        // Remove drivers that are no longer in data
        driverMarkerPool.current.forEach(({ marker, listener }, id) => {
            if (!currentIds.has(id)) {
                window.google.maps.event.removeListener(listener);
                marker.setMap(null);
                driverMarkerPool.current.delete(id);
            }
        });

        rebuildClusterBreakdownRef.current();
        rebuildPixelClustersRef.current();
    }, [map, drivers, bookings, zoom, showCompletedBookings, selectedBooking, showCriticalOnly, onSelectDriver, onSelectBooking]);

    // Helper: create a pulsing ring overlay at a marker's position using OverlayView
    const createPulseOverlay = useCallback((marker, color) => {
        if (!map || typeof window.google === 'undefined') return null;

        class PulseOverlay extends window.google.maps.OverlayView {
            constructor(position, pulseColor) {
                super();
                this.position = position;
                this.pulseColor = pulseColor;
                this.div = null;
            }
            onAdd() {
                this.div = document.createElement('div');
                this.div.style.position = 'absolute';
                this.div.style.pointerEvents = 'none';
                // Inner pulsing ring
                const ring = document.createElement('div');
                ring.className = 'ops-pulse-ring';
                ring.style.border = `3px solid ${this.pulseColor}`;
                this.div.appendChild(ring);
                const panes = this.getPanes();
                panes.overlayMouseTarget.appendChild(this.div);
            }
            draw() {
                if (!this.div) return;
                const overlayProjection = this.getProjection();
                if (!overlayProjection) return;
                const pos = overlayProjection.fromLatLngToDivPixel(this.position);
                if (pos) {
                    // Center the 28px ring on the pin's circle head
                    // Pin anchor is at bottom tip. Circle center is ~16px above tip at scale 1.8
                    this.div.style.left = (pos.x - 14) + 'px';
                    this.div.style.top = (pos.y - 14 - 16) + 'px';
                }
            }
            onRemove() {
                if (this.div && this.div.parentNode) {
                    this.div.parentNode.removeChild(this.div);
                    this.div = null;
                }
            }
            updatePosition(newPosition) {
                this.position = newPosition;
                this.draw();
            }
        }

        const overlay = new PulseOverlay(marker.getPosition(), color);
        overlay.setMap(map);
        return overlay;
    }, [map]);

    // Diff-based booking marker updates
    useEffect(() => {
        if (!map) return;

        const mapZoom = map.getZoom() ?? currentZoom.current ?? zoom;
        const offsetMap = buildMarkerOffsetMap({
            drivers: driversRef.current,
            bookings: bookingsRef.current,
            zoom: mapZoom,
            classFilterIds: classFilterDriverIdsRef.current,
            showCompletedBookings,
            selectedBooking,
            showCriticalOnly,
        });

        const currentIds = new Set();

        bookings.forEach((booking) => {
            const id = getBookingId(booking);
            if (!id) return;

            // Skip completed bookings — unless toggle is on OR this specific one is selected from sidebar
            if (booking.state === 'Completed' && !showCompletedBookings && (!selectedBooking || getBookingId(selectedBooking) !== id)) return;

            // Derive effective status
            let effectiveStatus = booking.state;
            if (booking.is_unassigned) {
                effectiveStatus = 'Unassigned';
            } else if (booking.escalation) {
                effectiveStatus = 'Delayed';
            } else if (booking.state === 'Pending') {
                const now = new Date();
                const pickupTime = new Date(booking.pickup_time);
                if (pickupTime < now) {
                    const isToday = pickupTime.toDateString() === now.toDateString();
                    effectiveStatus = isToday ? 'Overdue' : 'Unassigned';
                }
            }

            const isUrgent = effectiveStatus === 'Unassigned' || effectiveStatus === 'Delayed' || effectiveStatus === 'Overdue';

            // Apply Critical Only filter: Skip non-critical bookings when filter is ON
            if (showCriticalOnly && !isUrgent) {
                return;
            }

            currentIds.add(id);
            const trueLat = parseFloat(booking.pickup_lat);
            const trueLng = parseFloat(booking.pickup_lng || booking.pickup_lon);
            if (isNaN(trueLat) || isNaN(trueLng)) return;
            const { lat, lng } = getMarkerDisplayPosition(offsetMap, 'booking', id, trueLat, trueLng);

            // Temporal context: compute overdue label from backend escalation data
            let overdueBadge = '';
            if (effectiveStatus === 'Delayed' && booking.escalation) {
                const esc = booking.escalation;
                const typeLabel = esc.escalation_type === 'pickup_delay' ? 'Pickup Delay'
                    : esc.escalation_type === 'destination_delay' ? 'Drop Delay'
                        : esc.escalation_type === 'completion_delay' ? 'Completion Delay'
                            : 'Delayed';
                const mins = esc.time_overdue_minutes || 0;
                if (mins >= 60) {
                    overdueBadge = ` ${typeLabel} ⏱ ${Math.floor(mins / 60)}h ${mins % 60}m`;
                } else {
                    overdueBadge = ` ${typeLabel} ⏱ ${mins} min`;
                }
            } else if (effectiveStatus === 'Unassigned') {
                overdueBadge = ' ⚠ Unassigned';
            }
            const icon = getBookingIcon(effectiveStatus, mapZoom);
            // 1.5× size for urgent markers
            if (isUrgent) {
                icon.scale = icon.scale * 1.5;
            }

            const markerTitle = `Booking #${booking.reference_id}${overdueBadge}`;

            // Bounce-on-entry: bounce once when a booking first becomes urgent
            const wasUrgent = prevUrgencyRef.current.get(id);
            const justBecameUrgent = isUrgent && !wasUrgent;
            prevUrgencyRef.current.set(id, isUrgent);

            const existing = bookingMarkerPool.current.get(id);
            if (existing) {
                existing.trueLat = trueLat;
                existing.trueLng = trueLng;
                // Update position and icon
                const pos = existing.marker.getPosition();
                if (Math.abs(pos.lat() - lat) > 0.00001 || Math.abs(pos.lng() - lng) > 0.00001) {
                    existing.marker.setPosition({ lat, lng });
                }
                if (existing.pulseDiv?.updatePosition) {
                    existing.pulseDiv.updatePosition(new window.google.maps.LatLng(lat, lng));
                }
                existing.marker.setIcon(icon);
                existing.marker.setTitle(markerTitle);
                existing.marker.setZIndex(isUrgent ? 200 : 10);

                // Bounce once if just became urgent
                if (justBecameUrgent) {
                    existing.marker.setAnimation(window.google.maps.Animation.BOUNCE);
                    setTimeout(() => existing.marker.setAnimation(null), 1400);
                }

                // Add/remove pulse based on urgency change
                if (isUrgent && !existing.pulseDiv) {
                    const pulseColor = effectiveStatus === 'Unassigned' ? '#EF4444' : '#A855F7';
                    existing.pulseDiv = createPulseOverlay(existing.marker, pulseColor);
                } else if (!isUrgent && existing.pulseDiv) {
                    existing.pulseDiv.setMap(null);
                    existing.pulseDiv = null;
                }
            } else {
                // New booking — create marker
                const marker = new window.google.maps.Marker({
                    position: { lat, lng },
                    icon,
                    title: markerTitle,
                    zIndex: isUrgent ? 200 : 10,
                });

                // Bounce once on first appearance if already urgent
                if (isUrgent) {
                    marker.setAnimation(window.google.maps.Animation.BOUNCE);
                    setTimeout(() => marker.setAnimation(null), 1400);
                }

                const listener = marker.addListener('click', () => {
                    const key = locationKey(trueLat, trueLng);

                    onSelectDriver?.(null);

                    // Sort co-located by urgency
                    const urgencyScore = (b) => {
                        if (b.is_unassigned) return 0;
                        const st = b.state;
                        if (st === 'Delayed') return 1;
                        if (st === 'Pending') return 2;
                        if (st === 'Assigned') return 3;
                        return 4;
                    };
                    const coLocated = bookingsRef.current
                        .filter(b => {
                            const bLat = parseFloat(b.pickup_lat);
                            const bLng = parseFloat(b.pickup_lng || b.pickup_lon);
                            return locationKey(bLat, bLng) === key;
                        })
                        .sort((a, b) => urgencyScore(a) - urgencyScore(b));
                    setSelectedLocationBookings(coLocated);

                    // Find latest booking object
                    const latestBooking = bookingsRef.current.find(b => b.id === id);
                    onSelectBooking?.(latestBooking || booking);
                });

                let pulseDiv = null;
                if (isUrgent) {
                    const pulseColor = effectiveStatus === 'Unassigned' ? '#EF4444' : '#A855F7';
                    pulseDiv = createPulseOverlay(marker, pulseColor);
                }

                bookingMarkerPool.current.set(id, { marker, listener, pulseDiv, trueLat, trueLng });
                marker.setMap(map);
            }
        });

        // Remove bookings no longer in data
        bookingMarkerPool.current.forEach(({ marker, listener, pulseDiv }, id) => {
            if (!currentIds.has(id)) {
                window.google.maps.event.removeListener(listener);
                marker.setMap(null);
                if (pulseDiv) pulseDiv.setMap(null);
                bookingMarkerPool.current.delete(id);
            }
        });
    }, [map, drivers, bookings, zoom, selectedBooking, onSelectDriver, onSelectBooking, createPulseOverlay, showCriticalOnly, showCompletedBookings]);

    // Auto-fit bounds ONLY on initial data load
    React.useEffect(() => {
        if (hasFittedBounds.current) return; // Already fitted — preserve user's viewport
        if (mapViewport) {
            // Skip auto-fit if we have a saved viewport to restore
            hasFittedBounds.current = true;
            return;
        }
        if (map && (drivers.length > 0 || bookings.length > 0)) {
            const bounds = new window.google.maps.LatLngBounds();
            let hasPoints = false;

            drivers.forEach(driver => {
                const lat = parseFloat(driver.current_lat);
                const lng = parseFloat(driver.current_lng);
                if (!isNaN(lat) && !isNaN(lng)) {
                    bounds.extend({ lat, lng });
                    hasPoints = true;
                }
            });

            bookings.forEach(booking => {
                const lat = parseFloat(booking.pickup_lat);
                const lng = parseFloat(booking.pickup_lng || booking.pickup_lon);
                if (!isNaN(lat) && !isNaN(lng)) {
                    bounds.extend({ lat, lng });
                    hasPoints = true;
                }
            });

            if (trips && trips.length > 0) {
                trips.forEach(trip => {
                    const pLat = parseFloat(trip.pickup_lat);
                    const pLng = parseFloat(trip.pickup_lng || trip.pickup_lon);
                    const dLat = parseFloat(trip.drop_lat);
                    const dLng = parseFloat(trip.drop_lng || trip.drop_lon);
                    if (!isNaN(pLat) && !isNaN(pLng)) {
                        bounds.extend({ lat: pLat, lng: pLng });
                        hasPoints = true;
                    }
                    if (!isNaN(dLat) && !isNaN(dLng)) {
                        bounds.extend({ lat: dLat, lng: dLng });
                    }
                });
            }

            if (hasPoints) {
                map.fitBounds(bounds);
                hasFittedBounds.current = true;
                // Zoom out slightly if too zoomed in (e.g., single point)
                window.google.maps.event.addListenerOnce(map, "idle", () => {
                    if (map.getZoom() > 15) map.setZoom(15);
                });
            }
        }
    }, [map, drivers, bookings, trips, mapViewport]);

    if (loadError) {
        return <div className="text-danger p-3">Error loading map: {loadError.message}</div>;
    }

    if (!isLoaded) {
        return <div className="d-flex justify-content-center align-items-center h-100"><i className="fa fa-spinner fa-spin fa-3x text-muted"></i></div>;
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '600px' }}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapViewport ? mapViewport.center : defaultCenter}
                zoom={mapViewport ? mapViewport.zoom : 11}
                onLoad={onLoad}
                onIdle={() => {
                    // Do not save viewport if we haven't completed the initial fit bounds logic yet
                    if (!hasFittedBounds.current) return;

                    if (map && onViewportChange) {
                        try {
                            const newCenter = { lat: map.getCenter().lat(), lng: map.getCenter().lng() };
                            const newZoom = map.getZoom();
                            if (!mapViewport ||
                                mapViewport.zoom !== newZoom ||
                                Math.abs(mapViewport.center.lat - newCenter.lat) > 0.0001 ||
                                Math.abs(mapViewport.center.lng - newCenter.lng) > 0.0001) {
                                onViewportChange({ center: newCenter, zoom: newZoom });
                            }
                        } catch (e) {
                            console.error('Error saving map viewport', e);
                        }
                    }
                }}
                onUnmount={onUnmount}
                options={options}
                onClick={() => {
                    onSelectBooking?.(null);
                    setSelectedLocationBookings([]);
                    onSelectDriver?.(null);
                    selectedDriverRef.current = null;
                    setInfoWindowAnchor(null);
                    setSelectedLocationDrivers([]);
                    onClusterDriversSelectRef.current?.(null);
                    rebuildClusterBreakdownRef.current();
                }}
            >


                {map && (
                    <LeadDestinationHeatOverlay
                        map={map}
                        visible={!!showLeadHeatmap}
                        destinations={leadDestinations}
                        onSelect={onSelectLeadDestination}
                    />
                )}

                {/* Serviceable Zone Overlay */}
                {
                    showServiceZone && serviceZones.length > 0 && serviceZones.map((polygonCoords, index) => (
                        <Polygon
                            key={`zone-${index}`}
                            paths={polygonCoords}
                            options={{
                                fillColor: "#3c3d3eff",
                                fillOpacity: 0.15,  // Very light opacity
                                strokeOpacity: 0,  // CRUCIAL: 0 to hide honeycomb
                                strokeWeight: 0,
                                clickable: false,
                                zIndex: 0
                            }}
                        />
                    ))
                }
                {showLeadHeatmap && selectedLeadDestination && selectedLeadDestination.lat != null && (
                    <InfoWindow
                        position={{ lat: selectedLeadDestination.lat, lng: selectedLeadDestination.lng }}
                        onCloseClick={() => onSelectLeadDestination?.(null)}
                    >
                        <div className="ops-map-infowindow" style={{ minWidth: '200px', padding: '4px', color: '#1f2937', background: '#fff' }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                                {selectedLeadDestination.label || 'Destination'}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                <strong>Leads:</strong> {selectedLeadDestination.lead_count}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                <strong>Avg distance:</strong>{' '}
                                {selectedLeadDestination.avg_distance_km != null
                                    ? `${selectedLeadDestination.avg_distance_km} km`
                                    : 'n/a'}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                <strong>Tonnage:</strong>{' '}
                                {selectedLeadDestination.total_tonnes != null
                                    ? `${selectedLeadDestination.total_tonnes} t (${selectedLeadDestination.known_tonnage_count} parsed)`
                                    : 'unavailable'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                                {selectedLeadDestination.outbound_count || 0} outbound · {selectedLeadDestination.inbound_count || 0} inbound
                            </div>
                            {(selectedLeadDestination.leads || []).length > 0 && (
                                <div style={{ marginTop: '8px', borderTop: '1px solid #e5e7eb', paddingTop: '6px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                                        Leads (click to open)
                                    </div>
                                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                        {selectedLeadDestination.leads.map((lead) => (
                                            <button
                                                key={lead.lead_id}
                                                type="button"
                                                onClick={() => window.open(`/#/app/leads?expandLead=${lead.lead_id}`, '_blank', 'noopener')}
                                                title="Open this lead in the Leads page"
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    background: '#fff7ed',
                                                    border: '1px solid #fed7aa',
                                                    borderRadius: '4px',
                                                    padding: '4px 6px',
                                                    marginBottom: '4px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#9a3412' }}>
                                                    Lead #{lead.lead_id} · {(lead.status || 'unknown').replace('_', ' ')}
                                                    <i className="fa fa-external-link ml-1" />
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#64748b' }}>
                                                    {lead.pickup_label || 'Unknown pickup'} → {lead.direction}
                                                    {lead.distance_km != null ? ` · ${lead.distance_km} km` : ''}
                                                    {lead.tonnes != null ? ` · ${lead.tonnes} t` : ''}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    {selectedLeadDestination.leads_truncated && (
                                        <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                                            Showing first {selectedLeadDestination.leads.length} of {selectedLeadDestination.lead_count}.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </InfoWindow>
                )}
                {/* Hotzones Overlay — click polygon to see details */}
                {
                    showHotzones && hotzones.length > 0 && Object.values(hotzones
                        .filter((zone) => {
                            const src = zone.source || 'unknown';
                            if (src === 'realtime' && !showRealtimeHotzones) return false;
                            if ((src === 'historical' || src === 'unknown') && !showHistoricalHotzones) return false;
                            return true;
                        })
                        .reduce((acc, zone) => {
                            // Group by hotzone_id to handle overlapping classes
                            if (!acc[zone.hotzone_id]) {
                                acc[zone.hotzone_id] = {
                                    hotzone_id: zone.hotzone_id,
                                    polygon: zone.polygon,
                                    center: zone.center,
                                    isRealtime: false,
                                    zones: []
                                };
                            }
                            if (zone.source === 'realtime') acc[zone.hotzone_id].isRealtime = true;
                            acc[zone.hotzone_id].zones.push(zone);
                            return acc;
                        }, {}))
                        .map((group, index) => {
                            const isRealtime = group.isRealtime;
                            const fillColor = isRealtime ? '#EF4444' : '#F59E0B';
                            const strokeColor = isRealtime ? '#EF4444' : '#F59E0B';
                            return (
                                <Polygon
                                    key={`hotzone-group-${group.hotzone_id || index}`}
                                    paths={group.polygon}
                                    options={{
                                        fillColor: fillColor,
                                        fillOpacity: isRealtime ? 0.25 : 0.15,
                                        strokeColor: strokeColor,
                                        strokeOpacity: 0.6,
                                        strokeWeight: 1.5,
                                        clickable: true,
                                        zIndex: isRealtime ? 2 : 1
                                    }}
                                    onClick={() => setSelectedHotzone(group)}
                                />
                            );
                        })
                }
                {/* Hotzone InfoWindow — appears when a polygon is clicked */}
                {selectedHotzone && selectedHotzone.center && (
                    <InfoWindow
                        position={{ lat: selectedHotzone.center.lat, lng: selectedHotzone.center.lng }}
                        onCloseClick={() => setSelectedHotzone(null)}
                    >
                        <div className="ops-map-infowindow" style={{ minWidth: '180px', padding: '4px', color: '#1f2937', background: '#fff' }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '6px', color: selectedHotzone.isRealtime ? '#DC2626' : '#D97706' }}>
                                {selectedHotzone.isRealtime ? '🔴 Hotzone (Today\'s Demand)' : '🟡 Vehicle Recommendation'}
                            </div>
                            {selectedHotzone.zones.map((zone, idx) => (
                                <div key={idx} style={{ marginBottom: '8px', borderBottom: idx < selectedHotzone.zones.length - 1 ? '1px solid #e5e7eb' : 'none', paddingBottom: idx < selectedHotzone.zones.length - 1 ? '8px' : '0' }}>
                                    <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                                        <strong>Vehicle Class:</strong> {zone.vehicle_class_name || `Class ${zone.vehicle_class_id}`}
                                    </div>
                                    <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                        <strong>Demand:</strong> {zone.demand} &nbsp;|&nbsp; <strong>Supply:</strong> {zone.supply}
                                    </div>
                                    <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                                        <strong>Drivers Needed:</strong> {zone.gap}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </InfoWindow>
                )}
                {/* Pincode Coverage InfoWindow (Single Pincode View) */}
                {pincodeAnalysis && !pincodeAnalysis.is_city_wide && pincodeAnalysis.center && (
                    <>
                        <Marker
                            position={{ lat: pincodeAnalysis.center.lat, lng: pincodeAnalysis.center.lng }}
                            icon={getPincodeIcon()}
                            title={`Pincode ${pincodeAnalysis.pincode}`}
                        />
                        <InfoWindow
                            position={{ lat: pincodeAnalysis.center.lat, lng: pincodeAnalysis.center.lng }}
                        >
                            <div style={{ minWidth: '220px', padding: '6px', color: '#1e293b', background: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                                <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', color: '#0f172a' }}>
                                    <i className="fa fa-map-pin text-primary" style={{ marginRight: '6px' }} />
                                    Pincode {pincodeAnalysis.pincode}
                                </div>
                                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                                    <strong>Onboarded:</strong>{' '}
                                    {pincodeAnalysis.present_classes && pincodeAnalysis.present_classes.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>
                                            {pincodeAnalysis.present_classes.map(c => (
                                                <span key={c} style={{ background: '#10b981', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                                                    Class {c}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span style={{ color: '#64748b', fontStyle: 'italic' }}>None</span>
                                    )}
                                </div>
                                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                                    <strong style={{ color: '#ef4444' }}>Missing / Yet to Onboard:</strong>{' '}
                                    {pincodeAnalysis.missing_classes && pincodeAnalysis.missing_classes.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>
                                            {pincodeAnalysis.missing_classes.map(c => (
                                                <span key={c} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                                                    Class {c}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ color: '#10b981', fontWeight: 'bold', marginTop: '4px' }}>
                                            <i className="fa fa-check-circle" style={{ marginRight: '4px' }} /> Fully onboarded!
                                        </div>
                                    )}
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b', borderTop: '1px dashed #e2e8f0', paddingTop: '6px', marginTop: '6px' }}>
                                    Active vehicles in area: <strong>{pincodeAnalysis.vehicles?.length || 0}</strong>
                                </div>
                            </div>
                        </InfoWindow>
                    </>
                )}

                {/* City-wide Pincodes red triangles */}
                {pincodeAnalysis && pincodeAnalysis.is_city_wide && pincodeAnalysis.pincodes && pincodeAnalysis.pincodes.map(p => {
                    if (!p.center || typeof p.center.lat !== 'number' || typeof p.center.lng !== 'number') return null;
                    return (
                        <Marker
                            key={`pin-triangle-${p.pincode}`}
                            position={{ lat: p.center.lat, lng: p.center.lng }}
                            icon={getPincodeTriangleIcon(zoom)}
                            title={`Pincode ${p.pincode}`}
                            onClick={() => setClickedCityPincode(p)}
                        />
                    );
                })}

                {/* City-wide Active Clicked Pincode InfoWindow */}
                {pincodeAnalysis && pincodeAnalysis.is_city_wide && clickedCityPincode && clickedCityPincode.center && (
                    <InfoWindow
                        position={{ lat: clickedCityPincode.center.lat, lng: clickedCityPincode.center.lng }}
                        onCloseClick={() => setClickedCityPincode(null)}
                    >
                        <div style={{ minWidth: '220px', padding: '6px', color: '#1e293b', background: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                            <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', color: '#0f172a' }}>
                                <i className="fa fa-map-pin text-primary" style={{ marginRight: '6px' }} />
                                Pincode {clickedCityPincode.pincode}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                                <strong>Onboarded:</strong>{' '}
                                {clickedCityPincode.present_classes && clickedCityPincode.present_classes.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>
                                        {clickedCityPincode.present_classes.map(c => (
                                            <span key={c} style={{ background: '#10b981', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                                                Class {c}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span style={{ color: '#64748b', fontStyle: 'italic' }}>None</span>
                                )}
                            </div>
                            <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                                <strong style={{ color: '#ef4444' }}>Missing / Yet to Onboard:</strong>{' '}
                                {clickedCityPincode.missing_classes && clickedCityPincode.missing_classes.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>
                                        {clickedCityPincode.missing_classes.map(c => (
                                            <span key={c} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', fontWeight: 600 }}>
                                                Class {c}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ color: '#10b981', fontWeight: 'bold', marginTop: '4px' }}>
                                        <i className="fa fa-check-circle" style={{ marginRight: '4px' }} /> Fully onboarded!
                                    </div>
                                )}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', borderTop: '1px dashed #e2e8f0', paddingTop: '6px', marginTop: '6px' }}>
                                Active vehicles in area: <strong>{clickedCityPincode.vehicles_count || 0}</strong>
                            </div>
                        </div>
                    </InfoWindow>
                )}
                {/* Selected booking route: pickup → each ordered drop */}
                {selectedBooking && (() => {
                    const routePoints = getBookingRoutePoints(selectedBooking);
                    if (routePoints.length < 2) return null;
                    return (
                        <React.Fragment key={`booking-route-${getBookingId(selectedBooking)}`}>
                            <Polyline
                                path={routePoints}
                                options={{
                                    strokeColor: '#F97316',
                                    strokeOpacity: 0.95,
                                    strokeWeight: 5,
                                    geodesic: true,
                                    clickable: false,
                                    zIndex: 1200,
                                    icons: typeof window.google !== 'undefined' ? [{
                                        icon: {
                                            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                            scale: 2.5,
                                            strokeColor: '#C2410C',
                                            fillColor: '#C2410C',
                                            fillOpacity: 1,
                                        },
                                        offset: '58%',
                                    }] : undefined,
                                }}
                            />
                            {routePoints.slice(1).map((drop) => (
                                <Marker
                                    key={`booking-drop-${drop.index}-${drop.lat}-${drop.lng}`}
                                    position={drop}
                                    icon={getDropIcon(zoom)}
                                    label={routePoints.length > 2 ? {
                                        text: String(drop.index),
                                        color: '#ffffff',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                    } : undefined}
                                    title={`Drop ${drop.index}${drop.address ? `: ${drop.address}` : ''}`}
                                    zIndex={1300 + drop.index}
                                />
                            ))}
                        </React.Fragment>
                    );
                })()}

                {/* Active Trip Routes */}
                {trips && trips.length > 0 && trips.map((trip) => {
                    const pLat = parseFloat(trip.pickup_lat);
                    const pLng = parseFloat(trip.pickup_lng || trip.pickup_lon);
                    const dLat = parseFloat(trip.drop_lat);
                    const dLng = parseFloat(trip.drop_lng || trip.drop_lon);
                    if (isNaN(pLat) || isNaN(pLng) || isNaN(dLat) || isNaN(dLng)) return null;
                    const path = [{ lat: pLat, lng: pLng }, { lat: dLat, lng: dLng }];
                    return (
                        <Polyline
                            key={`trip-route-${trip.id || trip.trip_id}`}
                            path={path}
                            options={{
                                strokeColor: trip.state === 'Delayed' ? '#EF4444' : '#3B82F6', // Red for delayed, blue for ongoing
                                strokeOpacity: 0.8,
                                strokeWeight: 3,
                                strokePattern: [10, 5],
                                geodesic: true,
                            }}
                        />
                    );
                })}

                {/* Driver Info Window — scrollable when multiple drivers share the same location */}
                {
                    selectedDriver && (
                        <InfoWindow
                            key={`driver-iw-${selectedDriver.driver_id || selectedDriver.id}-${infoWindowAnchor ? 'anchored' : 'pos'}`}
                            anchor={infoWindowAnchor || undefined}
                            position={{
                                lat: parseFloat(selectedDriver.current_lat),
                                lng: parseFloat(selectedDriver.current_lng),
                            }}
                            options={
                                typeof window.google !== 'undefined'
                                    ? {
                                        zIndex: 2500,
                                        pixelOffset: new window.google.maps.Size(0, -4),
                                    }
                                    : undefined
                            }
                            onCloseClick={() => {
                                setInfoWindowAnchor(null);
                                onSelectDriver?.(null);
                                setSelectedLocationDrivers([]);
                                rebuildClusterBreakdownRef.current();
                            }}
                        >
                            <div className="ops-map-infowindow" style={{ minWidth: '280px', maxHeight: '320px', overflowY: 'auto', padding: '4px 2px', color: '#1f2937', background: '#fff' }}>
                                {(() => {
                                    const iwDrivers = selectedLocationDrivers.length > 0
                                        ? selectedLocationDrivers
                                        : [selectedDriver];
                                    return (
                                        <>
                                            {iwDrivers.length > 1 && (
                                                <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '6px', borderBottom: '2px solid #10B981', paddingBottom: '4px', color: '#1e3a5f' }}>
                                                    🚛 {iwDrivers.length} Vehicles at this location
                                                </div>
                                            )}
                                            {iwDrivers.map((drv, idx) => {
                                                const isActive = selectedDriver && (selectedDriver.driver_id || selectedDriver.id) === (drv.driver_id || drv.id);
                                                const vehiclePhoto = drv.vehicle_url || drv.url || drv.vehicle_photo_url;
                                                return (
                                                    <div
                                                        key={`${drv.driver_id || drv.id || idx}-${drv.vehicle_id || ''}`}
                                                        style={{
                                                            padding: '8px',
                                                            borderBottom: idx < iwDrivers.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                            backgroundColor: isActive 
                                                                ? (drv.is_transporter ? '#FDF2F8' : '#ECFDF5') 
                                                                : (idx % 2 === 0 ? '#fff' : '#f9fafb'),
                                                            borderLeft: isActive 
                                                                ? (drv.is_transporter ? '3px solid #db2777' : '3px solid #10B981') 
                                                                : (drv.is_transporter ? '3px solid #fbcfe8' : '3px solid transparent'),
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.15s',
                                                        }}
                                                        onClick={() => onSelectDriver?.(drv)}
                                                    >
                                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                                    <h6 style={{ fontWeight: 700, marginBottom: '0', fontSize: '14px', color: '#1f2937', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {drv.driver_name}
                                                                        {drv.vehicle_id && <span className="ml-1" style={{ fontWeight: 800 }}>({drv.vehicle_id})</span>}
                                                                    </h6>
                                                                    {drv.driver_status === 'registered' && drv.current_step && drv.current_step !== '-' && (
                                                                        <span style={{
                                                                            background: drv.current_step === 'completion' ? '#dcfce7' : '#fef08a',
                                                                            color: drv.current_step === 'completion' ? '#166534' : '#854d0e',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '6px',
                                                                            fontSize: '11px',
                                                                            fontWeight: '700',
                                                                            textTransform: 'capitalize',
                                                                            whiteSpace: 'nowrap',
                                                                            flexShrink: 0
                                                                        }}>
                                                                            {drv.current_step}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {drv.driver_status !== 'registered' && (
                                                                    <p style={{ marginBottom: '4px', color: '#6b7280', fontSize: '10px' }}>{drv.vehicle_registration}</p>
                                                                )}
                                                                <div style={{ marginBottom: '4px' }}>
                                                                    <span className={`badge badge-${drv.driver_status === 'active' ? 'primary' :
                                                                        drv.driver_status === 'available' ? 'success' :
                                                                            'secondary'
                                                                        }`} style={{ fontSize: '11px' }}>
                                                                        {drv.driver_status === 'active'
                                                                            ? (drv.current_trip_status || 'active').replace(/_/g, ' ')
                                                                            : (drv.driver_status || 'Unknown')}
                                                                    </span>
                                                                    <span className="badge badge-info ml-1" style={{ fontSize: '11px', textTransform: 'capitalize' }}>
                                                                        {String(drv.vehicle_class).replace('class', 'Class ')}
                                                                        {drv.vehicle_model && drv.vehicle_model !== 'N/A' ? ` - ${drv.vehicle_model}` : ''}
                                                                    </span>
                                                                    {drv.is_transporter && (
                                                                        <span className="badge ml-1" style={{
                                                                            fontSize: '11px',
                                                                            backgroundColor: '#db2777',
                                                                            color: '#ffffff',
                                                                            fontWeight: '600'
                                                                        }}>
                                                                            Transporter
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p style={{ marginBottom: '6px', fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>
                                                                    <i className="fa fa-phone mr-1" style={{ width: '12px', textAlign: 'center' }}></i> {drv.phone}
                                                                </p>
                                                                {drv.vehicle_id && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onViewVehicleDetails?.(drv);
                                                                        }}
                                                                        style={{
                                                                            background: '#2563eb',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            padding: '4px 10px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 600,
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        View Details
                                                                    </button>
                                                                )}
                                                                {drv.is_transporter && drv.transport_routes && drv.transport_routes.length > 0 && (
                                                                    <div style={{
                                                                        marginTop: '6px',
                                                                        fontSize: '11px',
                                                                        color: '#374151',
                                                                        backgroundColor: '#fdf2f8',
                                                                        border: '1px solid #fbcfe8',
                                                                        padding: '6px',
                                                                        borderRadius: '6px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '4px'
                                                                    }}>
                                                                        <div style={{ fontWeight: 700, color: '#db2777', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <span>📍 Routes:</span>
                                                                        </div>
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingLeft: '4px' }}>
                                                                            {drv.transport_routes.map((r, idx) => {
                                                                                const fromRaw = (r?.from?.address || '').trim();
                                                                                const toRaw = (r?.to?.address || '').trim();
                                                                                const fromLabel = fromRaw.split(',')[0].trim() || fromRaw || 'Any';
                                                                                const toLabel = toRaw.split(',')[0].trim() || toRaw || 'Any';
                                                                                return (
                                                                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500, color: '#4b5563' }}>
                                                                                        <span>{fromLabel}</span>
                                                                                        <span style={{ color: '#db2777' }}>→</span>
                                                                                        <span>{toLabel}</span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {vehiclePhoto && (
                                                                <div 
                                                                    style={{ flexShrink: 0, marginTop: '2px' }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setPreviewPhotoUrl(vehiclePhoto);
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={vehiclePhoto}
                                                                        alt="Vehicle"
                                                                        style={{
                                                                            width: '45px',
                                                                            height: '45px',
                                                                            borderRadius: '50%',
                                                                            objectFit: 'cover',
                                                                            border: '2px solid #10B981',
                                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                                                            cursor: 'pointer',
                                                                            transition: 'transform 0.15s'
                                                                        }}
                                                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    );
                                })()}
                            </div>
                        </InfoWindow>
                    )
                }

                {/* Booking Info Window — scrollable when multiple bookings share the same location */}
                {
                    selectedBooking && selectedLocationBookings.length > 0 && (
                        <InfoWindow
                            position={{ lat: parseFloat(selectedBooking.pickup_lat), lng: parseFloat(selectedBooking.pickup_lng || selectedBooking.pickup_lon) }}
                            options={typeof window.google !== 'undefined' ? {
                                maxWidth: BOOKING_INFO_WINDOW_WIDTH,
                                pixelOffset: getBookingInfoWindowOffset(
                                    pixelClusterProjectionRef.current,
                                    getBookingRoutePoints(selectedBooking)
                                ) || new window.google.maps.Size(0, 0),
                            } : undefined}
                            onCloseClick={() => { onSelectBooking?.(null); setSelectedLocationBookings([]); }}
                        >
                            <div className="ops-map-infowindow" style={{ minWidth: '300px', maxWidth: '380px', maxHeight: '360px', overflowY: 'auto', padding: '4px 2px', color: '#1f2937', background: '#fff' }}>
                                {selectedLocationBookings.length > 1 && (
                                    <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '6px', borderBottom: '2px solid #3B82F6', paddingBottom: '4px', color: '#1e3a5f' }}>
                                        <i className="fa fa-cubes" style={{ marginRight: '6px' }} />
                                        {selectedLocationBookings.length} bookings at this pickup
                                    </div>
                                )}
                                {selectedLocationBookings.map((bk, idx) => {
                                    const now = new Date();
                                    const pickupTime = new Date(bk.pickup_time);
                                    const isPastPickup = bk.state === 'Pending' && pickupTime < now;
                                    const isToday = pickupTime.toDateString() === now.toDateString();
                                    const isOverdue = isPastPickup && isToday;
                                    const isHistoricalUnassigned = isPastPickup && !isToday;
                                    const displayState = isOverdue ? 'Overdue' : isHistoricalUnassigned ? 'Unassigned' : bk.state;

                                    const statusColor = bk.is_unassigned || isHistoricalUnassigned
                                        ? '#EF4444'
                                        : bk.escalation || isOverdue
                                            ? '#A855F7'
                                            : displayState === 'Completed'
                                                ? '#10B981'
                                                : displayState === 'Pending'
                                                    ? '#FFFFFF'
                                                    : '#3B82F6';
                                    const statusLabel = bk.is_unassigned
                                        ? 'Unassigned'
                                        : bk.escalation
                                            ? (bk.escalation.escalation_type === 'pickup_delay' ? 'Pickup Delay'
                                                : bk.escalation.escalation_type === 'destination_delay' ? 'Drop Delay'
                                                    : bk.escalation.escalation_type === 'completion_delay' ? 'Completion Delay'
                                                        : 'Delayed')
                                            : (displayState || 'Unknown');
                                    const isLightBg = !bk.is_unassigned && !bk.escalation && displayState === 'Pending';
                                    const bookingId = getBookingId(bk);
                                    const isActive = selectedBooking && (getBookingId(selectedBooking) === bookingId);
                                    const bookingRoutePoints = getBookingRoutePoints(bk);
                                    return (
                                        <div
                                            key={bookingId || idx}
                                            style={{
                                                padding: '8px',
                                                borderBottom: idx < selectedLocationBookings.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                backgroundColor: isActive ? '#EBF5FF' : (idx % 2 === 0 ? '#fff' : '#f9fafb'),
                                                borderLeft: isActive ? '3px solid #3B82F6' : '3px solid transparent',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s',
                                            }}
                                            onClick={() => onSelectBooking?.(bk)}
                                            title="Click to show drop point"
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                <h6 style={{ fontWeight: 700, marginBottom: '0', fontSize: '13px', color: '#1f2937' }}>Booking #{bookingId}</h6>
                                                {bk.escalation && bk.escalation.severity && (
                                                    <span
                                                        style={{
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            backgroundColor: bk.escalation.severity === 'high' ? '#FEE2E2' : '#FEF3C7',
                                                            color: bk.escalation.severity === 'high' ? '#B91C1C' : '#92400E',
                                                            border: `1px solid ${bk.escalation.severity === 'high' ? '#FECACA' : '#FDE68A'}`,
                                                        }}
                                                    >
                                                        {bk.escalation.severity === 'high' ? '🔴 HIGH' : '🟡 MEDIUM'}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ marginBottom: '4px' }}>
                                                <span
                                                    className="badge"
                                                    style={{
                                                        backgroundColor: statusColor,
                                                        color: isLightBg ? '#000' : '#fff',
                                                        border: isLightBg ? '1px solid #ccc' : 'none',
                                                        fontSize: '11px',
                                                    }}
                                                >
                                                    {statusLabel}
                                                </span>
                                                <span className="badge badge-light border ml-1" style={{ fontSize: '11px' }}>{bk.vehicle_type}</span>
                                                {(() => {
                                                    if (bk.escalation) {
                                                        const mins = bk.escalation.time_overdue_minutes || 0;
                                                        if (mins > 0) {
                                                            const lateText = mins >= 60
                                                                ? `${Math.floor(mins / 60)}h ${mins % 60}m late`
                                                                : `${mins} min late`;
                                                            return (
                                                                <span
                                                                    className="badge ml-1"
                                                                    style={{
                                                                        backgroundColor: '#FEF2F2',
                                                                        color: '#B91C1C',
                                                                        border: '1px solid #FEE2E2',
                                                                        fontSize: '11px'
                                                                    }}
                                                                >
                                                                    ⏱ {lateText}
                                                                </span>
                                                            );
                                                        }
                                                    } else if (bk.is_unassigned && bk.pickup_time) {
                                                        const pickupTime = new Date(bk.pickup_time);
                                                        const now = new Date();
                                                        const diffMins = Math.floor((now - pickupTime) / 60000);
                                                        if (diffMins > 0) {
                                                            const lateText = diffMins >= 60
                                                                ? `${Math.floor(diffMins / 60)}h ${diffMins % 60}m late`
                                                                : `${diffMins} min late`;
                                                            return (
                                                                <span
                                                                    className="badge ml-1"
                                                                    style={{
                                                                        backgroundColor: '#FEF2F2',
                                                                        color: '#B91C1C',
                                                                        border: '1px solid #FEE2E2',
                                                                        fontSize: '11px'
                                                                    }}
                                                                >
                                                                    ⏱ {lateText}
                                                                </span>
                                                            );
                                                        }
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            <div style={{ marginBottom: '4px', color: '#4b5563', fontSize: '11px' }}>
                                                <i className="fa fa-clock-o mr-1"></i>
                                                {new Date(bk.pickup_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}, {new Date(bk.pickup_time).toLocaleDateString()}
                                            </div>
                                            <div style={{ marginTop: '7px', padding: '8px', borderRadius: '7px', background: '#fff', border: '1px solid #e5e7eb' }}>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '11px', color: '#374151' }}>
                                                    <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#2563eb', color: '#fff', textAlign: 'center', lineHeight: '18px', flexShrink: 0 }}>P</span>
                                                    <span><strong>Pickup</strong><br />{bk.pickup_address || 'Location unavailable'}</span>
                                                </div>
                                                {bookingRoutePoints.slice(1).map((drop) => (
                                                    <div key={`route-detail-${drop.index}-${drop.lat}-${drop.lng}`} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #e5e7eb', fontSize: '11px', color: '#374151' }}>
                                                        <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#f97316', color: '#fff', textAlign: 'center', lineHeight: '18px', flexShrink: 0 }}>{drop.index}</span>
                                                        <span><strong>Drop {bookingRoutePoints.length > 2 ? drop.index : ''}</strong><br />{drop.address || bk.drop_address || 'Location unavailable'}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {bk.assigned_driver_name && displayState === 'Assigned' && (
                                                <div style={{ marginTop: '6px', padding: '6px', backgroundColor: '#F3F4F6', borderRadius: '4px', borderLeft: '2px solid #6B7280' }}>
                                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#1f2937' }}>
                                                        🚛 {bk.assigned_driver_name}{bk.assigned_vehicle_id ? ` (${bk.assigned_vehicle_id})` : ''}
                                                    </p>
                                                    {(bk.assigned_vehicle_class || bk.assigned_vehicle_make || bk.assigned_vehicle_model) && (
                                                        <p style={{ margin: 0, fontSize: '10px', color: '#6B7280' }}>
                                                            {[
                                                                bk.assigned_vehicle_class ? `Class ${bk.assigned_vehicle_class}` : null,
                                                                [bk.assigned_vehicle_make, bk.assigned_vehicle_model].filter(Boolean).join(' ') || null
                                                            ].filter(Boolean).join(' - ')}
                                                        </p>
                                                    )}
                                                    <p style={{ margin: 0, fontSize: '10px', color: '#4B5563' }}>📞 {bk.assigned_driver_phone}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </InfoWindow>
                    )
                }
            </GoogleMap >
            {previewPhotoUrl && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(15, 23, 42, 0.9)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'zoom-out'
                    }}
                    onClick={() => setPreviewPhotoUrl(null)}
                >
                    <div 
                        style={{ 
                            position: 'relative', 
                            maxWidth: '90%', 
                            maxHeight: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src={previewPhotoUrl} 
                            alt="Vehicle Preview" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '80vh', 
                                borderRadius: '8px', 
                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
                                border: '4px solid #fff'
                            }} 
                        />
                        <button
                            style={{
                                position: 'absolute',
                                top: '-45px',
                                right: '-10px',
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                color: '#fff',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s'
                            }}
                            onClick={() => setPreviewPhotoUrl(null)}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.4)'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(OpsMapView);
